<template>
    <p class="q-mb-sm"><strong>Form Type</strong></p>
    <q-select v-model="_formType" :options="formTypeOptions" placeholder="Select a form type" outlined dense map-options emit-value
        option-label="name" option-value="id" :loading="loadingOptions">
        <template v-slot:option="scope">
            <q-item v-bind="scope.itemProps">
                <q-item-section>
                    <q-item-label>{{ scope.opt.name }}</q-item-label>
                    <q-item-label v-if="scope.opt.description" caption>{{ scope.opt.description }}</q-item-label>
                </q-item-section>
            </q-item>
        </template>
    </q-select>

    <p class="q-mb-sm"><strong>Form Select</strong></p>
    <q-select v-model="_formId" :options="filteredFormOptions" placeholder="Select the form to fill out" outlined dense map-options emit-value
        option-label="title" option-value="id" :loading="loadingOptions" :disable="_formType === null">
        <template v-slot:option="scope">
            <q-item v-bind="scope.itemProps">
                <q-item-section>
                    <q-item-label>{{ scope.opt.title }}</q-item-label>
                    <q-item-label caption>{{ scope.opt.description }}</q-item-label>
                </q-item-section>
            </q-item>
        </template>
    </q-select>
</template>

<script>
    import { collapseFormFamilies } from '@/services/util/formVersions';
    import api from "@/services/api/api";
    import notify from "@/services/util/notify";

    export default {
        name: "FormSelector",
        emits: ['update:formId', 'update:form'],
        props: {
            formId: {
                type: [Number, String, null],
                default: null
            }
        },
        data() {
            return {
                loadingOptions: false,
                formOptions: [],
                selectedFormType: null
            }
        },
        computed: {
            formTypeOptions() {
                const types = new Map();

                this.formOptions.forEach(form => {
                    const id = this.getFormTypeKey(form);
                    if (!types.has(id)) {
                        types.set(id, {
                            id,
                            name: form.formTypeName || "Other Services",
                            description: form.formTypeDescription || "Forms for other services in Te Kohinga."
                        });
                    }
                });

                return [...types.values()];
            },
            filteredFormOptions() {
                return this.formOptions.filter(form => this.getFormTypeKey(form) === this.selectedFormType);
            },
            _formType: {
                get() {
                    return this.selectedFormType;
                },
                set(value) {
                    this.selectedFormType = value;
                    const firstForm = this.formOptions.find(form => this.getFormTypeKey(form) === value);
                    this._formId = firstForm?.id ?? null;
                }
            },
            _formId: {
                get() {
                    return this.formId;
                },
                set(value) {
                    this.$emit('update:formId', value);
                    this.$emit('update:form', this.findForm(value));
                }
            }
        },
        methods: {
            getFormTypeKey(form) {
                return form.formTypeId === null ? "uncategorised" : `type:${form.formTypeId}`;
            },
            findForm(formId) {
                return this.formOptions.find(form => String(form.id) === String(formId)) || null;
            },
            async fetchFormOptions() {
                this.loadingOptions = true;
                try {
                    const response = await api.forms.getDynamicFormTypes();
                    this.formOptions = collapseFormFamilies(response.data.data);

                    const selectedForm = this.findForm(this.formId);
                    if (selectedForm) {
                        this.selectedFormType = this.getFormTypeKey(selectedForm);
                        this.$emit('update:form', selectedForm);
                    } else if (this.formTypeOptions.length > 0) {
                        this._formType = this.formTypeOptions[0].id;
                    }
                } catch (error) {
                    console.error("Error fetching form options:", error);
                    notify.error("Failed to load form types. Please try again later.", "top");
                } finally {
                    this.loadingOptions = false;
                }
            }
        },
        created() {
            this.fetchFormOptions();
        }
    }
</script>
